import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalStorage } from '@utils/storage';
import { useQueryParamHelper } from '@utils/queryString';
import { addError } from '@utils/logger';
import { withWindow } from '@utils/withWindow';
import { BUILD_LANGUAGE, DEFAULT_LANGUAGE } from '@i18n/languages';

// `/stat/js/joy` must be dynamically required as it lives
// outside the TS build scope and is copied at build time
const analytics = require('/static/js/joy');

export const CULTURE_PARAMS = ['cul', 'culture'];
export const CURRENCY_PARAMS = ['cur', 'currency'];
export const LANGUAGE_PARAMS = ['l', 'lng', 'lang', 'language'];

export enum CurrencyCode {
  AUD = 'AUD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  USD = 'USD',
}

export enum CultureCode {
  AU = 'AU',
  CA = 'CA',
  DE = 'DE',
  ES = 'ES',
  FR = 'FR',
  IT = 'IT',
  GB = 'GB',
  JP = 'JP',
  US = 'US',
}

export enum LanguageCode {
  de = 'de',
  en = 'en',
  es = 'es',
  fr = 'fr',
  it = 'it',
  ja = 'ja',
}

const findMatchingEnumValue = <T>(enumObj: Record<string, T>, key?: string): T | undefined => {
  if (!key) {
    return undefined;
  }

  const actualKey = Object.keys(enumObj).find((val) => val?.toLowerCase() === key.toLowerCase());
  return actualKey ? enumObj[actualKey] : undefined;
};

type GeoBasedLocalePayload = Readonly<{
  culture: CultureCode;
  currency: CurrencyCode;
  language: LanguageCode;
}>;

const DefaultLocalePayload: GeoBasedLocalePayload = {
  currency: CurrencyCode.USD,
  culture: CultureCode.US,
  language: LanguageCode.en,
};

const defaultLocales: ReadonlyArray<GeoBasedLocalePayload> = [
  DefaultLocalePayload,
  {
    culture: CultureCode.AU,
    currency: CurrencyCode.AUD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.DE,
    currency: CurrencyCode.EUR,
    language: LanguageCode.de,
  },
  {
    culture: CultureCode.ES,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.FR,
    currency: CurrencyCode.EUR,
    language: LanguageCode.fr,
  },
  {
    culture: CultureCode.IT,
    currency: CurrencyCode.EUR,
    language: LanguageCode.it,
  },
  {
    culture: CultureCode.US,
    currency: CurrencyCode.USD,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.GB,
    currency: CurrencyCode.GBP,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.JP,
    currency: CurrencyCode.JPY,
    language: LanguageCode.ja,
  },
];

const findClosestMatchingLocale = (
  payload?: Partial<{
    culture: string;
    currency: string;
    language: string;
  }>
): GeoBasedLocalePayload | undefined => {
  if (!payload) {
    return undefined;
  }

  // handle extended language codes (eg. en-AU, es-US)
  const [languageCode, cultureCode] = payload.language?.split('-') ?? [];

  const culture = findMatchingEnumValue(CultureCode, payload.culture ?? cultureCode);
  const currency = findMatchingEnumValue(CurrencyCode, payload.currency);
  const language = findMatchingEnumValue(LanguageCode, languageCode);

  // Skip processing if no valid parameters found
  if (!culture && !currency && !language) {
    return undefined;
  }

  const countMatchingProperties = (locale: GeoBasedLocalePayload): number => {
    return (locale.language === language ? 1 : 0) + (locale.culture === culture ? 1 : 0) + (locale.currency === currency ? 1 : 0);
  };

  // Find the locale from defaultLocales that has the most matching properties
  return defaultLocales.reduce((bestMatch, locale) => {
    const currentScore = countMatchingProperties(locale);
    const bestScore = bestMatch ? countMatchingProperties(bestMatch) : 0;

    return currentScore > bestScore ? locale : bestMatch;
  });
};

const constructLanguageCodeIso15897 = (language: LanguageCode, culture: CultureCode): string => {
  return `${language}-${culture}`;
};

export const updateLocaleLocalStorage = (updatedLocale: Partial<GeoBasedLocalePayload>) => {
  const locale = findClosestMatchingLocale(updatedLocale);
  if (locale) {
    localStorage.setItem('joyLocale', JSON.stringify(locale));
  }
};

export const updateLocaleSearchParams = (updatedLocale: Partial<GeoBasedLocalePayload>) => {
  const locale = findClosestMatchingLocale(updatedLocale);
  if (!locale) {
    return;
  }

  const url = new URL(window.location.href);
  const searchParams = url.searchParams;

  const updateParams = (params: string[], addParamIfMissing: boolean, value?: string) => {
    if (!value) {
      return false;
    }
    let updated = false;
    params.forEach((param) => {
      if (searchParams.has(param)) {
        searchParams.set(param, value);
        updated = true;
      }
    });

    if (addParamIfMissing && !updated) {
      searchParams.set(params[0], value);
      updated = true;
    }

    return updated;
  };

  const removeParams = (params: string[]) => {
    let updated = false;
    params.forEach((param) => {
      if (searchParams.has(param)) {
        searchParams.delete(param);
        updated = true;
      }
    });
    return updated;
  };

  const { languageCodeIso15897 } = extendLocale(locale);
  const isDefaultLanguage = languageCodeIso15897 === DEFAULT_LANGUAGE;

  const cultureParamsUpdated = updateParams(CULTURE_PARAMS, false, locale.culture);
  const currencyParamsUpdate = updateParams(CURRENCY_PARAMS, false, locale.currency);
  const languageParamsUpdated = isDefaultLanguage
    ? removeParams(LANGUAGE_PARAMS) // remove default `en-US` language param
    : updateParams(LANGUAGE_PARAMS, true, languageCodeIso15897); // always set the language query param for non-default languages

  if (cultureParamsUpdated || currencyParamsUpdate || languageParamsUpdated) {
    history.replaceState({}, '', url.toString());
  }
};

const getLocallyStoredGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const localStorage = getLocalStorage();
  const storedLocale = localStorage.getItem('joyLocale');

  if (!storedLocale) {
    return undefined;
  }

  try {
    const locale = findClosestMatchingLocale(JSON.parse(storedLocale));
    return locale;
  } catch (error) {
    addError(error as Error, {});
  }
  return undefined;
};

const useSearchParamsGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const { getValueString } = useQueryParamHelper();

  const culture = getValueString(CULTURE_PARAMS);
  const currency = getValueString(CURRENCY_PARAMS);
  const language = getValueString(LANGUAGE_PARAMS);

  return findClosestMatchingLocale({ culture, currency, language });
};

const useBrowserLanguageGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const browserLanguage = withWindow((window) => window.navigator.language, undefined);

  return findClosestMatchingLocale({ language: browserLanguage });
};

const extendLocale = (locale: GeoBasedLocalePayload): GeoBasedLocaleExtended => {
  return {
    ...locale,
    languageCodeIso15897: constructLanguageCodeIso15897(locale.language, locale.culture),
  };
};

type GeoBasedLocaleExtended = GeoBasedLocalePayload & {
  languageCodeIso15897: string;
  countryCodeAlpha2?: string;
  countryName?: string;
};

export const useGeoBasedLocale = (defaultValues?: GeoBasedLocalePayload) => {
  const searchParamLocale = useSearchParamsGeoLocale();
  const localStorageLocale = getLocallyStoredGeoLocale();
  const browserLocale = useBrowserLanguageGeoLocale();

  const staticBuildLocale = findClosestMatchingLocale({
    language: BUILD_LANGUAGE,
  });

  const fallbackLocale = defaultValues ?? DefaultLocalePayload;

  // in priority order
  const detectionOrder = [
    ['gatsbyStaticBuildLocale', staticBuildLocale],
    ['searchParamLocale', searchParamLocale],
    ['localStorageLocale', localStorageLocale],
    ['browserLocale', browserLocale],
  ];

  const [localeDetectionMethod, locale] = detectionOrder.find(
    (entry): entry is [string, GeoBasedLocalePayload] => entry[1] !== undefined
  ) ?? ['fallbackLocale', fallbackLocale];

  return { locale: extendLocale(locale), localeDetectionMethod };
};

// sync detected locale to i18n
export const useI18nLocaleSyncer = () => {
  const { locale, localeDetectionMethod } = useGeoBasedLocale();
  const { i18n } = useTranslation();

  const { culture, currency, language, languageCodeIso15897 } = locale;

  useEffect(() => {
    // slice languageCodeIso15897 so the culture code isn't compared when i18n.resolvedLanguage doesn't contain it
    // Example: i18n.resolvedLanguage = 'ja', languageCodeIso15897 = 'ja-JA'
    const detectedLanguage = languageCodeIso15897.slice(0, i18n.resolvedLanguage?.length);
    if (i18n.resolvedLanguage !== detectedLanguage) {
      analytics.segment.sendTelemetryEvent('MarcomLocaleSynced', {
        extraInfo: {
          fromLanguage: i18n.language,
          toLanguage: languageCodeIso15897,
          localeDetectionMethod,
        },
      });
    }

    updateLocaleLocalStorage(locale);
    updateLocaleSearchParams(locale);

    i18n.changeLanguage(languageCodeIso15897);

    // use culture, currency and language in the dependency array to prevent retriggering when the 'locale' object's
    // reference is updated, but the values are the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culture, currency, language, languageCodeIso15897, localeDetectionMethod, i18n]);
};
